import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography,
} from "@mui/material";

import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

import { HTTP_METHODS, invokeApi } from "../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../common-utilities/utils/end-points/finance-v2";
import { getFinancialYears } from "../home/financial-year-dropdown";
import MyPrTracking from "./my-pr-tracking";
import DashboardFilters from "./dashboard-filters";
import { buildFilterParams } from "./dashboard-filters/utils";

import "./index.scss";
import { getOfflineData } from "../../utils/offline-services";

const isVendorUser = getOfflineData("user")?.role?.toLowerCase().includes("vendor") || false;

const CARD_SUMMARY_KEY = {
  "My Pending Actions": "myPendingActions",
  Budgets: "budget",
  PR: "pr",
  PO: "po",
  "Goods Receive Notes": "grn",
  "Advance Payment Details": "advancePayment",
};

const STATUS_LABEL_KEY = {
  Draft: "draft",
  "Approval In Progress": "approvalInProgress",
  Approved: "approved",
  "Approved POs": "approved",
  Rejected: "rejected",
  Reconsider: "reconsider",
  "Vendor Made GRN": "vendorMadeGrn",
  Pending: "pending",
  "On Hold": "onHold",
  "Awaiting Approval": "awaitingApproval",
  "PO Released": "poReleased",
  "Pending PO Creation": "pendingPoCreation",
  "Open POs": "open",
  "Closed POs": "closed",
};

const getCardIcon = (label = "") => {
  const normalizedLabel = String(label).toLowerCase();

  if (normalizedLabel.includes("pending")) return <PendingActionsOutlinedIcon />;
  if (normalizedLabel.includes("budget")) return <AccountBalanceWalletOutlinedIcon />;

  if (
    normalizedLabel === "pr" ||
    normalizedLabel.includes("purchase requisition")
  ) {
    return <ReceiptLongOutlinedIcon />;
  }

  if (
    normalizedLabel === "po" ||
    normalizedLabel.includes("purchase order")
  ) {
    return <ShoppingCartCheckoutOutlinedIcon />;
  }

  if (
    normalizedLabel.includes("grn") ||
    normalizedLabel.includes("goods receive")
  ) {
    return <Inventory2OutlinedIcon />;
  }

  if (
    normalizedLabel.includes("advance") ||
    normalizedLabel.includes("payment")
  ) {
    return <PaymentsOutlinedIcon />;
  }

  if (normalizedLabel.includes("report")) return <AssessmentOutlinedIcon />;
  if (normalizedLabel.includes("master")) return <SettingsOutlinedIcon />;

  if (
    normalizedLabel.includes("approval") ||
    normalizedLabel.includes("mapping") ||
    normalizedLabel.includes("config")
  ) {
    return <TuneOutlinedIcon />;
  }

  return <AssessmentOutlinedIcon />;
};

const FinanceDashboard = ({ cards = [], onViewTab }) => {
  const [summary, setSummary] = useState(null);
  const { defaultFY } = getFinancialYears();
  const [filters, setFilters] = useState({ financialYear: defaultFY });

  useEffect(() => {
    if (!isVendorUser && !filters.financialYear) return;

    let isCurrent = true;
    setSummary(null);

    const summaryParams = buildFilterParams(filters, { isVendorBranch: isVendorUser });

    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_DASHBOARD_SUMMARY}`,
      null,
      summaryParams
    )
      .then((response) => {
        if (!isCurrent) return;
        if (!response?.message) setSummary(response);
      })
      .catch(() => {});

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.financialYear, filters.region, filters.site, filters.vendorCode, isVendorUser]);

  const getStatusValue = (card, status) => {
    const value =
      summary?.[CARD_SUMMARY_KEY[card.label]]?.[STATUS_LABEL_KEY[status.label]];
    return value ?? status.value;
  };

  const handleViewTab = (tabValue) => {
    if (typeof onViewTab === "function") {
      onViewTab(tabValue);
    }
  };

  const statCards = cards.filter(
    (card) => (card.statusBreakdown || []).length > 0
  );

  const quickLinkCards = cards.filter(
    (card) => !(card.statusBreakdown || []).length
  );

  const topStatCards = statCards.slice(0, 4);
  const bottomStatCards = statCards.slice(4);

  
  const renderStatCard = (card) => (
    <Grid item xs={12} sm={6} lg={3} key={card.tabValue}>
      <Card className="finance-dashboard-stat-card" variant="outlined">
        <CardActionArea
          className="finance-dashboard-card-action"
          onClick={() => handleViewTab(card.tabValue)}
        >
          <CardContent className="finance-dashboard-card-content">
            <Box className="finance-dashboard-card-top">
              <Box className="finance-dashboard-icon">
                {getCardIcon(card.label)}
              </Box>

              <Box className="finance-dashboard-card-text">
                <Typography className="finance-dashboard-card-title">
                  {card.label}
                </Typography>

                <Typography className="finance-dashboard-card-desc">
                  {card.description}
                </Typography>
              </Box>
            </Box>

            <Divider className="finance-dashboard-card-divider" />

            <Box className="finance-dashboard-card-status">
              {card.statusBreakdown.map((status) => (
                <Box className="finance-dashboard-status-row" key={status.label}>
                  <Typography className="finance-dashboard-status-label">
                    {status.label}
                  </Typography>

                  <Chip
                    className="finance-dashboard-status-value"
                    label={getStatusValue(card, status) ?? "--"}
                    size="small"
                  />
                </Box>
              ))}
            </Box>

            <Box className="finance-dashboard-card-footer">
              <Button
                className="finance-dashboard-card-view"
                variant="outlined"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleViewTab(card.tabValue);
                }}
              >
                View
              </Button>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

  const renderQuickLinkCard = (card) => (
    <Grid item xs={12} sm={6} key={card.tabValue}>
      <Card className="finance-dashboard-link-card" variant="outlined">
        <CardActionArea
          className="finance-dashboard-card-action"
          onClick={() => handleViewTab(card.tabValue)}
        >
          <CardContent className="finance-dashboard-link-content">
            <Box className="finance-dashboard-card-top">
              <Box className="finance-dashboard-icon">
                {getCardIcon(card.label)}
              </Box>

              <Box className="finance-dashboard-card-text">
                <Typography className="finance-dashboard-card-title">
                  {card.label}
                </Typography>

                <Typography className="finance-dashboard-card-desc">
                  {card.description}
                </Typography>
              </Box>
            </Box>

            <Box className="finance-dashboard-link-footer">
              <Button
                className="finance-dashboard-card-view"
                variant="outlined"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleViewTab(card.tabValue);
                }}
              >
                View
              </Button>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

 
  return (
    <Box className="finance-v2-dashboard">
      {!isVendorUser && (
        <Box className="finance-dashboard-filter-row">
          <DashboardFilters filters={filters} setFilters={setFilters} isVendorBranch={isVendorUser} />
        </Box>
      )}

      <Grid container spacing={2}>
        {topStatCards.map(renderStatCard)}
      </Grid>

      <Grid container spacing={2} className="finance-dashboard-second-row">
        {bottomStatCards.map(renderStatCard)}

        {quickLinkCards.length > 0 && (
          <Grid item xs={12} lg={6}>
            <Grid container spacing={2} className="finance-dashboard-quick-grid">
              {quickLinkCards.map(renderQuickLinkCard)}
            </Grid>
          </Grid>
        )}
      </Grid>

      <MyPrTracking
        filters={filters}
        prTrackingSummary={summary?.pr?.prTrackingSummary}
        onViewAll={() =>
          handleViewTab(cards.find((card) => card.label === "PR")?.tabValue)
        }
      />
    </Box>
  );
};

export default FinanceDashboard;